import {useCallback, useEffect, useRef, useState} from 'react';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {Link} from 'react-router-dom';
import {format} from 'date-fns';
import {ArrowSquareOut, Bug as BugIcon} from '@phosphor-icons/react';
import {useAuth} from '../hooks/useAuth';
import {useAppVersions} from '../hooks/useAppVersions';
import {AffectedScope} from '../components/AffectedScope';
import {prodVersionLabels, VersionList} from '../components/ProdVersions';
import {checkShipment} from '../services/shipment';
import type {ShipmentCheck} from '../services/shipment';
import {ContentSampleLine, ContentTags} from '../components/ContentTags';
import {
    findFixCommits,
    listBugs,
    resolveFixVersion,
    updateBugFields,
    updateBugStatus,
} from '../services/bugService';
import type {FixCommitSearch} from '../services/bugService';
import {
    BUG_SEVERITY_COLORS,
    BUG_SEVERITY_LABELS,
    BUG_STATUS_COLORS,
    BUG_STATUS_LABELS,
    BUG_TERMINAL_STATUSES,
} from '../types/bug';
import type {Bug, BugStatus} from '../types/bug';
import {bugSynthesisPath, feedbackPath} from '../routes';

const ALL_STATUSES: BugStatus[] = [
    'detected', 'verified', 'fixed', 'shipped', 'not_reproduced', 'wont_fix',
];

/** Un bug livre sans numero de version est intracable : on le demande. */
function needsFixVersion(status: BugStatus, bug: Bug): boolean {
    return status === 'shipped' && !bug.fixVersion;
}

/**
 * Le commit se cherche au passage en « Corrigé » ou « Livré », pas plus tard :
 * c'est le seul moment ou quelqu'un l'a encore en tete. Un bug ne venant que
 * d'un feedback n'a aucune cle (pas d'issue Sentry a citer dans le message de
 * commit), donc rien ne permettra de le retrouver ensuite.
 *
 * On ne le demande plus a la main : la recherche part seule et propose. Saisir
 * un SHA de memoire dans une boite de dialogue etait le moyen le plus sur de
 * se tromper d'un caractere.
 */
function shouldSearchCommit(status: BugStatus, bug: Bug): boolean {
    return (status === 'fixed' || status === 'shipped') && !bug.fixCommit;
}

/**
 * Le commit propose est-il celui deja enregistre ?
 *
 * Comparaison par prefixe : le champ peut contenir un SHA court saisi a la main
 * la ou la recherche renvoie le SHA complet.
 */
function isKeptCommit(bug: Bug, hash: string): boolean {
    const kept = bug.fixCommit;
    if (!kept) return false;
    return hash.startsWith(kept) || kept.startsWith(hash);
}

const REPO_COMMIT_URL = 'https://bitbucket.org/mantano/flutter-main/commits/';

interface ShipmentHintProps {
    bug: Bug;
    check: ShipmentCheck;
    busy: boolean;
    onShip: () => void;
}

/**
 * Où en est le correctif par rapport aux stores.
 *
 * Trois situations à ne pas confondre : il est chez les utilisateurs (alors
 * autant passer le bug en « Livré », c'est le geste attendu), il n'y est pas
 * encore (le dire évite de prévenir les signalants trop tôt), ou il n'y est que
 * sur une partie des plateformes — cas le plus trompeur, puisque le bug paraît
 * réglé alors que la moitié des signalants l'a toujours.
 *
 * Rien n'est déclaré livré automatiquement : la comparaison ne sait pas si la
 * version publiée contient vraiment ce correctif, seulement qu'elle est
 * postérieure.
 */
function ShipmentHint({bug, check, busy, onShip}: ShipmentHintProps) {
    if (!bug.fixVersion || check.state === 'unknown') return null;

    if (bug.status === 'shipped') {
        // Incoherence a signaler : annonce faite, correctif pas publie.
        return check.waiting.length > 0 ? (
            <span className="text-amber-600">
                déclaré livré, mais pas encore en production sur {check.waiting.join(', ')}
            </span>
        ) : null;
    }

    if (check.state === 'shipped') {
        return (
            <button onClick={onShip} disabled={busy} className={ACTION_PRIMARY_CLASS}>
                En production sur {check.live.join(', ')} — passer en livré
            </button>
        );
    }
    if (check.state === 'partial') {
        const pending = [
            ...check.waiting.map((p) => `pas encore sur ${p}`),
            ...check.unknown.map((p) => `version inconnue sur ${p}`),
        ];
        return (
            <span className="text-amber-600">
                {check.live.length > 0 ? `en production sur ${check.live.join(', ')}, ` : ''}
                {pending.join(', ')}
            </span>
        );
    }
    return (
        <span className="text-amber-600">
            pas encore en production ({check.waiting.join(', ')})
        </span>
    );
}

interface BugNoteProps {
    bug: Bug;
    busy: boolean;
    onSave: (note: string) => Promise<void>;
}

/**
 * Le commentaire de l'équipe sur un bug.
 *
 * Un seul par bug, réécrit à chaque fois : ce qu'il faut savoir maintenant —
 * la piste suivie, le contournement donné au signalant, la raison d'attendre —
 * pas le journal de ce qui a été dit. Une fiche de bug se lit en diagonale au
 * moment de trier ; un fil de discussion s'y serait vite empilé sans que
 * personne ne le relise.
 *
 * Une saisie vide efface : `updateBugFields` traite la chaîne vide comme une
 * suppression de champ, il n'y a donc pas de bouton « Supprimer » de plus.
 */
function BugNote({bug, busy, onSave}: BugNoteProps) {
    const t = useTraduire();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');

    // Le brouillon repart du texte enregistré à chaque ouverture : la liste se
    // recharge après chaque écriture, et un brouillon gardé d'une fois sur
    // l'autre réafficherait un texte déjà remplacé.
    const start = () => {
        setDraft(bug.note ?? '');
        setEditing(true);
    };

    const save = async () => {
        await onSave(draft.trim());
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="mt-3 pt-3 border-t border-gray-100">
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    autoFocus
                    placeholder="Ce qu'il faut savoir sur ce bug : piste suivie, contournement, décision prise…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <button onClick={() => void save()} disabled={busy} className={ACTION_PRIMARY_CLASS}>
                        Enregistrer
                    </button>
                    <button onClick={() => setEditing(false)} className={ACTION_CLASS}>
                        Annuler
                    </button>
                    {bug.note && !draft.trim() && (
                        <span className="text-xs text-amber-600">
                            Enregistrer un texte vide efface le commentaire.
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3 pt-3 border-t border-gray-100">
            {bug.note ? (
                <div className="flex items-start gap-2 text-xs">
                    <span className="shrink-0 text-gray-400 mt-0.5">{t(T.commentaire)}</span>
                    <p className="min-w-0 flex-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
                        {bug.note}
                    </p>
                    <button onClick={start} className={`shrink-0 ${ACTION_CLASS}`}>{t(T.modifier)}</button>
                </div>
            ) : (
                <button onClick={start} className={`text-xs ${ACTION_CLASS}`}>+ commentaire</button>
            )}
        </div>
    );
}

/** Action secondaire : discrète mais identifiable comme cliquable. */
const ACTION_CLASS =
    'px-2 py-1 rounded border border-gray-300 text-xs text-gray-600 bg-white hover:bg-gray-50 hover:border-primary hover:text-primary transition-colors disabled:opacity-50';
/** Action attendue à cet instant : elle doit sauter aux yeux. */
const ACTION_PRIMARY_CLASS =
    'px-2.5 py-1 rounded border border-primary text-xs text-primary bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-50';

export function BugsPage() {
    const t = useTraduire();
    const {user} = useAuth();
    const {versions: appVersions, storeVersions} = useAppVersions();
    const prodVersions = prodVersionLabels(appVersions, storeVersions);
    const [bugs, setBugs] = useState<Bug[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<BugStatus | ''>('');
    const [showTerminal, setShowTerminal] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [search, setSearch] = useState<Record<string, FixCommitSearch | 'searching' | string>>({});
    const [autoShipped, setAutoShipped] = useState<string[]>([]);
    const [autoVersioned, setAutoVersioned] = useState<string[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setBugs(await listBugs(statusFilter || undefined));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load bugs');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        void load();
    }, [load]);

    const visible = bugs.filter(
        (b) => showTerminal || statusFilter !== '' || !BUG_TERMINAL_STATUSES.includes(b.status),
    );

    /**
     * Passage automatique en « Livré » au chargement.
     *
     * Un bug corrigé dont la version corrective est publiée sur toutes les
     * plateformes qu'il touche — ou sur toutes, s'il n'en nomme aucune — est
     * livré : le constater à la main sur chaque fiche ne rendait le statut juste
     * que le jour où quelqu'un pensait à le regarder.
     *
     * Deux garde-fous : seuls les bugs au statut « Corrigé » sont concernés, et
     * une plateforme dont la version publiée est inconnue suffit à s'abstenir.
     * Le passage est annoncé au-dessus de la liste : une écriture invisible sur
     * un statut terminal serait indéfendable.
     *
     * Ce que la comparaison ne sait pas : que la version publiée contient bien
     * ce correctif. Elle sait qu'elle lui est postérieure — la version de
     * livraison ayant été saisie pour ce bug, c'est la même chose en pratique.
     */
    /**
     * Version de livraison déduite du commit, au chargement.
     *
     * Saisie à la main, elle était fausse une fois sur deux : on retient la
     * version en cours de développement, pas celle qui a effectivement embarqué
     * le correctif. Le dépôt le sait — c'est le premier tag qui contient le
     * commit — et c'est cette version qui décide ensuite si le bug est livré.
     *
     * Retenté à chaque chargement tant qu'aucune ne répond : un correctif mergé
     * aujourd'hui sera publié plus tard, et le statut doit se corriger seul ce
     * jour-là plutôt qu'attendre qu'on y repense.
     */
    const resolvedVersionIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!user || loading) return;
        const missing = bugs.filter(
            (b) => b.fixCommit && !b.fixVersion && !resolvedVersionIds.current.has(b.id),
        );
        if (missing.length === 0) return;
        for (const b of missing) resolvedVersionIds.current.add(b.id);
        void (async () => {
            const found: string[] = [];
            for (const b of missing) {
                try {
                    const resolution = await resolveFixVersion(b.fixCommit!);
                    if (!resolution.version) continue;
                    await updateBugFields(b.id, {fixVersion: resolution.version}, user.uid);
                    found.push(`${b.title} — v${resolution.version} (${resolution.tag})`);
                } catch (e) {
                    // Un commit introuvable ou un jeton expiré ne doit pas
                    // empêcher la page de fonctionner : la saisie à la main reste.
                    console.warn('[bugs] version de livraison non résolue:', e);
                }
            }
            if (found.length === 0) return;
            setAutoVersioned(found);
            await load();
        })();
    }, [bugs, user, loading, load]);

    const autoShippedIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!user || loading) return;
        const eligible = bugs.filter(
            (b) => b.status === 'fixed'
                && !autoShippedIds.current.has(b.id)
                && checkShipment(b.fixVersion, b.platforms, appVersions, storeVersions)
                    .state === 'shipped',
        );
        if (eligible.length === 0) return;
        // Marque avant d'ecrire : sans ca, un rendu declenche par le premier
        // succes relancerait la meme mise a jour.
        for (const b of eligible) autoShippedIds.current.add(b.id);
        void (async () => {
            try {
                for (const b of eligible) {
                    await updateBugStatus(b.id, 'shipped', user.uid, b.fixVersion);
                }
                setAutoShipped(eligible.map((b) => `${b.title} — v${b.fixVersion}`));
                await load();
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Passage en livré impossible');
            }
        })();
    }, [bugs, user, loading, appVersions, storeVersions, load]);

    const handleStatus = async (bug: Bug, status: BugStatus) => {
        if (!user) return;
        let fixVersion: string | undefined;
        if (needsFixVersion(status, bug)) {
            const answer = window.prompt(
                `Version dans laquelle le correctif est livré (ex. 5.3.1) :`,
                '',
            );
            if (answer === null) return; // annule
            fixVersion = answer.trim() || undefined;
        }
        setBusy(bug.id);
        try {
            await updateBugStatus(bug.id, status, user.uid, fixVersion);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
            return;
        } finally {
            setBusy(null);
        }
        // Le bug vient d'etre declare corrige : la recherche part sans qu'on la
        // demande, et propose. Rien n'est enregistre avant « Retenir ».
        if (shouldSearchCommit(status, bug)) void handleSearchCommit(bug);
    };

    const handleField = async (
        bug: Bug,
        field: 'prUrl' | 'regressionTestRef' | 'fixVersion' | 'fixCommit',
        current?: string,
    ) => {
        if (!user) return;
        const labels = {
            prUrl: 'URL de la pull request',
            regressionTestRef: 'Référence du test de non-régression',
            fixVersion: 'Version de livraison',
            fixCommit: 'SHA du commit qui corrige',
        };
        const answer = window.prompt(`${labels[field]} :`, current || '');
        if (answer === null) return;
        setBusy(bug.id);
        try {
            await updateBugFields(bug.id, {[field]: answer.trim()}, user.uid);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        } finally {
            setBusy(null);
        }
    };

    /** Écriture séparée de handleField : le commentaire se saisit sur place,
     *  pas dans une boîte de dialogue qui ne tient pas trois lignes. */
    const handleNote = async (bug: Bug, note: string) => {
        if (!user) return;
        setBusy(bug.id);
        try {
            await updateBugFields(bug.id, {note}, user.uid);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        } finally {
            setBusy(null);
        }
    };

    const handleSearchCommit = async (bug: Bug) => {
        setSearch((p) => ({...p, [bug.id]: 'searching'}));
        try {
            const found = await findFixCommits(bug.id);
            setSearch((p) => ({...p, [bug.id]: found}));
        } catch (e) {
            setSearch((p) => ({...p, [bug.id]: e instanceof Error ? e.message : 'Recherche impossible'}));
        }
    };

    const handleKeepCommit = async (bug: Bug, hash: string) => {
        if (!user) return;
        setBusy(bug.id);
        try {
            await updateBugFields(bug.id, {fixCommit: hash}, user.uid);
            setSearch((p) => {
                const {[bug.id]: _drop, ...rest} = p;
                return rest;
            });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Update failed');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-6">
                <div>
                    <h1 className="text-2xl font-medium text-gray-900 flex items-center gap-2">
                        <BugIcon size={26} weight="regular" className="text-primary"/>
                        Bugs
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Bugs promus depuis les candidats, et leur cycle de vie
                    </p>
                    {/* Versions en production : c'est a elles qu'on compare une
                        version de livraison pour savoir si un correctif est
                        vraiment chez les utilisateurs. Relevees sur les stores,
                        donc plus a jour que le document Firestore, que le hook
                        remet a jour au passage. */}
                    {prodVersions.length > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                            En production : <VersionList labels={prodVersions}/>
                        </p>
                    )}
                </div>
                <Link
                    to={bugSynthesisPath}
                    className="shrink-0 text-sm text-primary hover:underline"
                >
                    Proposer de nouveaux candidats →
                </Link>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-5 mb-6">
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6 sm:items-end">
                    <div className="w-full sm:w-52">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t(T.colonneEtat)}</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as BugStatus | '')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        >
                            <option value="">{t(T.tousLesStatuts)}</option>
                            {ALL_STATUSES.map((s) => (
                                <option key={s} value={s}>{BUG_STATUS_LABELS[s]}</option>
                            ))}
                        </select>
                    </div>
                    {statusFilter === '' && (
                        <label className="flex items-center gap-2 text-sm text-gray-600 sm:pb-2">
                            <input
                                type="checkbox"
                                checked={showTerminal}
                                onChange={(e) => setShowTerminal(e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            Afficher les bugs clos
                        </label>
                    )}
                    <button
                        onClick={() => void load()}
                        className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50"
                    >
                        Rafraîchir
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {error}
                </div>
            )}

            {autoVersioned.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg mb-6">
                    <p className="text-xs text-blue-800">
                        Version de livraison déduite du commit pour {autoVersioned.length} bug
                        {autoVersioned.length !== 1 ? 's' : ''} : c'est le premier tag du dépôt
                        qui contient le correctif.
                    </p>
                    <ul className="mt-1 space-y-0.5">
                        {autoVersioned.map((label) => (
                            <li key={label} className="text-xs text-blue-700 flex gap-1.5">
                                <span className="text-blue-300">·</span><span>{label}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {autoShipped.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-lg mb-6">
                    <p className="text-xs text-emerald-800">
                        {autoShipped.length} bug{autoShipped.length !== 1 ? 's' : ''} passé
                        {autoShipped.length !== 1 ? 's' : ''} en « Livré » : la version
                        corrective est publiée sur toutes les plateformes concernées.
                    </p>
                    <ul className="mt-1 space-y-0.5">
                        {autoShipped.map((label) => (
                            <li key={label} className="text-xs text-emerald-700 flex gap-1.5">
                                <span className="text-emerald-400">·</span><span>{label}</span>
                            </li>
                        ))}
                    </ul>
                    {/* Les signalants ne sont pas prévenus pour autant : c'est un
                        envoi, il se décide depuis la fiche du signalement. */}
                    <p className="mt-1 text-xs text-emerald-700">
                        Les signalants n'ont pas été prévenus : cet envoi reste à faire à la main.
                    </p>
                </div>
            )}

            {loading && (
                <div className="bg-white rounded-lg shadow p-8 text-sm text-gray-500">{t(T.chargement)}</div>
            )}

            {!loading && visible.length === 0 && (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t(T.aucunBug)}</h3>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                        Les bugs apparaissent ici une fois qu'un candidat a été promu depuis la
                        page Détecter. Rien n'est créé automatiquement.
                    </p>
                </div>
            )}

            {!loading && visible.length > 0 && (
                <div className="space-y-4">
                    {visible.map((bug) => (
                        <div key={bug.id} className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="px-4 sm:px-5 py-4">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${BUG_SEVERITY_COLORS[bug.severity]}`}>
                                                {BUG_SEVERITY_LABELS[bug.severity]}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${BUG_STATUS_COLORS[bug.status]}`}>
                                                {BUG_STATUS_LABELS[bug.status]}
                                            </span>
                                            {/* Constat repris de la détection, pas un
                                                arbitrage fait ici : l'app avait signalé
                                                la situation sans que rien casse. */}
                                            {bug.impactsUsers === false && (
                                                <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                                    N'impacte pas les utilisateurs
                                                </span>
                                            )}
                                            <h3 className="text-sm font-medium text-gray-900 break-words">{bug.title}</h3>
                                        </div>
                                        {bug.rationale && (
                                            <p className="text-sm text-gray-600 leading-relaxed">{bug.rationale}</p>
                                        )}
                                        {/* Quel contenu est en cause : figé à la
                                            promotion, donc affiché sans compteurs
                                            qui ne bougeraient plus. */}
                                        <div className="mt-2 space-y-1">
                                            <ContentTags tags={bug.contentTags} showCounts={false}/>
                                            <ContentSampleLine sample={bug.contentSample}/>
                                        </div>
                                    </div>

                                    <select
                                        value={bug.status}
                                        disabled={busy === bug.id}
                                        onChange={(e) => void handleStatus(bug, e.target.value as BugStatus)}
                                        className="shrink-0 self-start w-full sm:w-auto px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white disabled:opacity-50"
                                    >
                                        {ALL_STATUSES.map((s) => (
                                            <option key={s} value={s}>{BUG_STATUS_LABELS[s]}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-500">
                                    {/* Portee du bug, dans les memes termes que sur la
                                        page Détecter : elle est reprise du candidat au
                                        moment de la promotion. La derniere occurrence
                                        Sentry primes sur le dernier signalement : elle
                                        couvre tous les utilisateurs, pas seulement ceux
                                        qui ont ecrit. */}
                                    <AffectedScope
                                        platforms={bug.platforms}
                                        versions={bug.affectedVersions}
                                        lastOccurrence={
                                            bug.lastSeenAt?.toDate?.()
                                            ?? bug.lastReportedAt?.toDate?.()
                                            ?? null
                                        }
                                    />
                                    <span>
                                        {bug.feedbackIds.length} signalement{bug.feedbackIds.length !== 1 ? 's' : ''}
                                    </span>
                                    {bug.userCount ? <span>{bug.userCount} utilisateur(s) Sentry</span> : null}
                                    {bug.createdAt?.toDate && (
                                        <span>créé le {format(bug.createdAt.toDate(), 'dd/MM/yyyy')}</span>
                                    )}
                                    {bug.feedbackIds.length === 0 && (
                                        <span className="text-amber-600">{t(T.aucunSignalantAPrevenir)}</span>
                                    )}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    {/* Nouvel onglet : on repond au signalant sans
                                        quitter la liste des bugs en cours de tri. */}
                                    {bug.feedbackIds.length > 0 && (
                                        <span className="text-xs text-gray-400">{t(T.repondreAuSignalant)}</span>
                                    )}
                                    {bug.feedbackIds.map((id) => (
                                        <Link
                                            key={id}
                                            to={feedbackPath(id)}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={t(T.ouvrirLeSignalement)}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-primary/20 bg-primary/5 text-xs text-primary hover:bg-primary/10"
                                        >
                                            #{id.slice(0, 8)}
                                            <ArrowSquareOut size={12} weight="regular"/>
                                        </Link>
                                    ))}
                                    {bug.sentryUrl && (
                                        <a
                                            href={bug.sentryUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="px-2 py-0.5 rounded bg-purple-50 text-xs text-purple-700 hover:bg-purple-100"
                                        >
                                            {bug.sentryShortId || 'Sentry'}
                                        </a>
                                    )}
                                </div>

                                {/* Volet 2 : les traces du correctif */}
                                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 text-xs">
                                    <span className="text-gray-400 mr-1">{t(T.correctif)}</span>
                                    <button
                                        onClick={() => void handleField(bug, 'fixVersion', bug.fixVersion)}
                                        className={ACTION_CLASS}
                                    >
                                        {bug.fixVersion ? `Corrigé en v${bug.fixVersion}` : '+ version de livraison'}
                                    </button>
                                    {/* « Corrigé en v5.3.2 » ne dit pas si les
                                        utilisateurs l'ont : la version publiée sur
                                        chaque store le dit, elle. */}
                                    <ShipmentHint
                                        bug={bug}
                                        check={checkShipment(
                                            bug.fixVersion,
                                            bug.platforms,
                                            appVersions,
                                            storeVersions,
                                        )}
                                        busy={busy === bug.id}
                                        onShip={() => void handleStatus(bug, 'shipped')}
                                    />
                                    {bug.fixCommit && (
                                        <a
                                            href={`${REPO_COMMIT_URL}${bug.fixCommit}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-primary hover:underline font-mono"
                                        >
                                            {bug.fixCommit.slice(0, 9)}
                                        </a>
                                    )}
                                    {/* La recherche reste offerte meme quand un commit
                                        est deja retenu : une erreur de rattachement se
                                        corrige en relancant et en retenant un autre
                                        commit, pas en retapant un SHA de memoire. */}
                                    <button
                                        onClick={() => void handleSearchCommit(bug)}
                                        disabled={search[bug.id] === 'searching'}
                                        className={
                                            // Un bug corrigé sans commit : c'est LE geste attendu.
                                            !bug.fixCommit
                                            && (bug.status === 'fixed' || bug.status === 'shipped')
                                                ? ACTION_PRIMARY_CLASS
                                                : ACTION_CLASS
                                        }
                                    >
                                        {search[bug.id] === 'searching'
                                            ? 'Recherche du commit…'
                                            : bug.fixCommit
                                                ? '⌕ Changer de commit'
                                                : '⌕ Chercher le commit'}
                                    </button>
                                    <button
                                        onClick={() => void handleField(bug, 'fixCommit', bug.fixCommit)}
                                        className={ACTION_CLASS}
                                    >
                                        Saisir à la main
                                    </button>
                                    {bug.prUrl ? (
                                        <a href={bug.prUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                                            Pull request
                                        </a>
                                    ) : (
                                        <button
                                            onClick={() => void handleField(bug, 'prUrl')}
                                            className={ACTION_CLASS}
                                        >
                                            + pull request
                                        </button>
                                    )}
                                    <button
                                        onClick={() => void handleField(bug, 'regressionTestRef', bug.regressionTestRef)}
                                        className={ACTION_CLASS}
                                    >
                                        {bug.regressionTestRef
                                            ? `Test : ${bug.regressionTestRef}`
                                            : '+ test de non-régression'}
                                    </button>
                                    {bug.status === 'shipped' && bug.feedbackIds.length > 0 && (
                                        <span className="text-amber-600">
                                            {(bug.notifiedFeedbackIds?.length ?? 0)}/{bug.feedbackIds.length} signalant(s) prévenu(s)
                                        </span>
                                    )}
                                </div>

                                {/* Commits proposés : la fonction propose, l'humain retient. */}
                                {typeof search[bug.id] === 'string' && search[bug.id] !== 'searching' && (
                                    <div className="mt-2 text-xs text-red-600">{search[bug.id] as string}</div>
                                )}
                                {typeof search[bug.id] === 'object' && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                        {(search[bug.id] as FixCommitSearch).candidates.length === 0 ? (
                                            <p className="text-xs text-gray-500">
                                                Aucun commit plausible parmi les{' '}
                                                {(search[bug.id] as FixCommitSearch).scanned} derniers. Le correctif est
                                                peut-être plus ancien, ou sur une autre branche.
                                            </p>
                                        ) : (
                                            <>
                                                <p className="text-xs text-gray-400 mb-2">
                                                    {(search[bug.id] as FixCommitSearch).mode === 'exact'
                                                        ? 'Trouvé par la clé Sentry citée dans le message'
                                                        : `Rapprochement sur ${(search[bug.id] as FixCommitSearch).scanned} commits — à valider`}
                                                </p>
                                                <div className="space-y-2">
                                                    {(search[bug.id] as FixCommitSearch).candidates.map((c) => (
                                                        <div key={c.hash} className="flex items-start gap-3">
                                                            {/* Le commit deja retenu est signale, pas
                                                                propose : le reproposer laisserait croire
                                                                qu'il n'a pas ete choisi. */}
                                                            {isKeptCommit(bug, c.hash) ? (
                                                                <span className="shrink-0 px-2 py-1 rounded bg-emerald-50 text-xs text-emerald-700">
                                                                    Retenu
                                                                </span>
                                                            ) : (
                                                            <button
                                                                onClick={() => void handleKeepCommit(bug, c.hash)}
                                                                disabled={busy === bug.id}
                                                                className="shrink-0 px-2 py-1 rounded border border-gray-300 text-xs text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50"
                                                            >
                                                                {bug.fixCommit ? 'Remplacer' : 'Retenir'}
                                                            </button>
                                                            )}
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <a
                                                                        href={c.url}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="text-xs font-mono text-primary hover:underline"
                                                                    >
                                                                        {c.shortHash}
                                                                    </a>
                                                                    <span className="text-xs text-gray-400">{c.date}</span>
                                                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                                        c.confidence === 'high'
                                                                            ? 'bg-emerald-100 text-emerald-800'
                                                                            : c.confidence === 'medium'
                                                                                ? 'bg-amber-100 text-amber-800'
                                                                                : 'bg-gray-100 text-gray-500'
                                                                    }`}>
                                                                        {c.confidence}
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs text-gray-700 mt-0.5">{c.message}</p>
                                                                <p className="text-xs text-gray-500">{c.why}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <BugNote
                                    bug={bug}
                                    busy={busy === bug.id}
                                    onSave={(note) => handleNote(bug, note)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
